import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { fetchPlayerProfile } from './api';
import { useAsyncResource } from './hooks';
import PlayerIdentityCard from '../../components/PlayerIdentityCard';
import PlayerLoader from '../../components/PlayerLoader';
import PlayerBillingSection from '../../components/PlayerBillingSection';
import ReferralSection from '../../components/ReferralSection';
import {
  ONBOARDING_FLAG_KEY,
  PLAYER_ONBOARDING_REOPEN_EVENT,
} from '../../components/PlayerOnboarding/constants';
import { userActions } from '../../store/user/userSlice';
import { useLexicon } from '../../hooks/useLexicon';

const interp = (tmpl, nodes) => {
  const parts = String(tmpl).split(/\{[^}]+\}/);
  return parts.flatMap((p, i) => (i < nodes.length ? [p, nodes[i]] : [p]));
};

const PlayerProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const l = useLexicon();
  const subscription = useSelector((state) => state.user.subscription);
  const { data, loading, error } = useAsyncResource(fetchPlayerProfile, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    dispatch(userActions.setToken(''));
    dispatch(userActions.clearAuthContext());
    navigate('/login');
  };

  const handleReplayOnboarding = () => {
    localStorage.removeItem(ONBOARDING_FLAG_KEY);
    window.dispatchEvent(new Event(PLAYER_ONBOARDING_REOPEN_EVENT));
  };

  const payload = data?.data ?? data?.results ?? data;
  const user = payload?.user;
  const player = payload?.player;

  const displayName = player?.display_name
    || [player?.name, player?.surname].filter(Boolean).join(' ')
    || [user?.first_name, user?.last_name].filter(Boolean).join(' ')
    || user?.email;

  const nationality = player?.nationality?.data?.[0]?.name
    || player?.nationality?.[0]?.name;
  const position = player?.position?.data?.[0]?.name
    || player?.position?.[0]?.name;

  return (
    <Wrap>
      <Title>{l(1796)}</Title>

      {loading && <PlayerLoader label={l(1797)} />}
      {error && <ErrorBox>{l(1798)}</ErrorBox>}

      {!loading && !error && payload && (
        <>
          {player && <PlayerIdentityCard player={player} displayName={displayName} />}

          <Section>
            <SectionTitle>{l(1799)}</SectionTitle>
            <Row><Label>{l(1739)}</Label><Value>{user?.email || '—'}</Value></Row>
            <Row><Label>{l(1735)}</Label><Value>{user?.first_name || '—'}</Value></Row>
            <Row><Label>{l(1737)}</Label><Value>{user?.last_name || '—'}</Value></Row>
          </Section>

          <PlayerBillingSection subscription={subscription} />

          <ReferralSection />

          {player && (
            <Section>
              <SectionTitle>{l(1800)}</SectionTitle>
              {player.nickname && <Row><Label>{l(1801)}</Label><Value>{player.nickname}</Value></Row>}
              {player.birth_date && (
                <Row>
                  <Label>{l(1802)}</Label>
                  <Value>{new Date(player.birth_date).toLocaleDateString()}</Value>
                </Row>
              )}
              {position && <Row><Label>{l(11)}</Label><Value>{position}</Value></Row>}
              {nationality && <Row><Label>{l(111)}</Label><Value>{nationality}</Value></Row>}
              {player.leg && <Row><Label>{l(1803)}</Label><Value>{player.leg}</Value></Row>}
              {player.height && <Row><Label>{l(113)}</Label><Value>{player.height} cm</Value></Row>}
              {player.weight && <Row><Label>{l(1006)}</Label><Value>{player.weight} kg</Value></Row>}
            </Section>
          )}

          <Section>
            <SectionTitle>{l(1804)}</SectionTitle>
            <ActionRow type="button" onClick={handleReplayOnboarding}>
              <Label>{l(1805)}</Label>
              <Chevron aria-hidden>›</Chevron>
            </ActionRow>
          </Section>

          <Section>
            <SectionTitle>{l(1806)}</SectionTitle>
            <SupportRow>
              <SupportText>
                {interp(l(1807), [
                  <SupportLink key="s" href="mailto:support@smrtstats.com">
                    support@smrtstats.com
                  </SupportLink>,
                ])}
              </SupportText>
            </SupportRow>
          </Section>

          <LogoutButton type="button" onClick={handleLogout}>{l(1808)}</LogoutButton>
        </>
      )}
    </Wrap>
  );
};

export default PlayerProfile;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
  margin: 0;
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
  padding: 8px 0;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 8px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionTitle = styled.h2`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(props) => props.theme.blueish2};
  margin: 4px 0 0;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${(props) => props.theme.row};
  border-radius: 8px;
`;

const ActionRow = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: ${(props) => props.theme.row};
  border: none;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  color: ${(props) => props.theme.white};
  font-family: inherit;
`;

const Chevron = styled.span`
  color: ${(props) => props.theme.blueish2};
  font-size: 20px;
  line-height: 1;
`;

const Label = styled.span`
  color: ${(props) => props.theme.blueish2};
  font-size: 13px;
`;

const Value = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
`;

const SupportRow = styled.div`
  padding: 14px 16px;
  background: ${(props) => props.theme.row};
  border-radius: 8px;
`;

const SupportText = styled.p`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.blueish2};
  line-height: 1.5;
  margin: 0;
`;

const SupportLink = styled.a`
  color: ${(props) => props.theme.green};
  text-decoration: none;
  font-family: 'SatoshiBold', sans-serif;

  &:hover { text-decoration: underline; }
`;

const LogoutButton = styled.button`
  margin-top: 8px;
  background: transparent;
  border: 1px solid ${(props) => props.theme.lines};
  color: ${(props) => props.theme.white};
  padding: 14px;
  border-radius: 8px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  cursor: pointer;
`;
